import { useState, useEffect, useRef, useCallback } from 'react';
import { updateEpisodes } from '../system-episodes';
import {
    aggregateStatus as foldAggregate,
    classifyHttpStatus,
    createStatusConfirmer,
    notChecked,
    reachableOnly,
    timedOut,
    unreachable,
} from '../health-rules';

/**
 * Service definitions — each entry describes a Cloud Run service to health-check.
 * URLs come from sysconfig; fallback derives from the Cloud Run naming pattern.
 *
 * What a probe result means lives in health-rules.js, not here. The public status
 * page uses the same file, so the button and the website cannot disagree.
 */
const SERVICE_DEFS = [
    { key: 'sam_dbam', label: 'sam-dbam', healthPath: '/dbam/v1/health', configKey: 'sam_dbam' },
    // Identity administration (password reset, org transfer, user delete) writes
    // straight into Hanko's database and refuses to run unless its configuration
    // and schema assertion hold. sam-dbam stays healthy without it, so it needs
    // its own row: reported as part of sam-dbam's status it was invisible, and
    // the first anyone knew of a two-year outage was an operator being told
    // "identity administration is temporarily unavailable".
    { key: 'identity_admin', label: 'Identity Admin', healthPath: '/dbam/v1/health', configKey: 'sam_dbam', isIdentityAdmin: true },
    { key: 'kernel', label: 'Kernel', healthPath: '/api/v5/health', configKey: 'kernel' },
    { key: 'console', label: 'Console', healthPath: null, configKey: 'console', isSelf: true },
    { key: 'designer', label: 'Designer', healthPath: '/', configKey: 'designer' },
    { key: 'explorer', label: 'Explorer', healthPath: '/health', configKey: 'explorer' },
    { key: 'channel_gate', label: 'Channel Gate', healthPath: '/', configKey: 'channel_gate', fallbackUrl: 'https://channel-gate-453954531541.us-central1.run.app' },
    // /health is the only route this service defines. It was probed at "/" for
    // months, which answers 404, and the 404 was published to customers as
    // "Degraded" for a service that was working normally.
    { key: 'survey_assistant', label: 'Survey Assistant', healthPath: '/health', configKey: 'survey_assistant', fallbackUrl: 'https://survey-assistant-453954531541.us-central1.run.app' },
    // Email is deliberately absent. This list is the platform's own services.
    // Email is a channel, and channel status is managed in the Channels context,
    // which owns both the reading and the detail. Probing it from here also cost
    // one request to the console backend on every poll — every 10 to 45 seconds,
    // for every open tab — which filled the Cloud Run log with tens of thousands
    // of entries that nobody read.
];

/**
 * How long to wait for an answer.
 *
 * These services scale to zero, so a cold start of several seconds is normal
 * operation rather than a fault. The old four-second limit turned ordinary cold
 * starts into red alarms, most often on tablets and phones where the network
 * adds latency of its own.
 */
const PROBE_TIMEOUT_MS = 10_000;

/** How many recent polls to carry inside a filed record, for after-the-fact reading. */
const POLL_HISTORY_LIMIT = 40;

/** Where undelivered records wait when the write fails. */
const PENDING_ALERTS_KEY = 'sam.systemStatus.pendingAlerts';
const PENDING_ALERTS_LIMIT = 20;
/**
 * Give up on a record after this many tries. A packet the backend will never
 * accept — one the store rejects outright, say — would otherwise be re-sent on
 * every poll for as long as the tab stayed open.
 */
const PENDING_ALERT_MAX_ATTEMPTS = 10;

/**
 * Derive fallback URL from sam-dbam URL pattern.
 * e.g. https://sam-dbam-453954531541.us-central1.run.app → replace service name
 */
function deriveFallbackUrl(samDbamUrl, serviceName) {
    if (!samDbamUrl) return null;
    try {
        const u = new URL(samDbamUrl);
        // Pattern: {service}-{project-number}.{region}.run.app
        const parts = u.hostname.split('.');
        if (parts.length >= 1) {
            // Replace the service segment (everything before the first number segment)
            const firstPart = parts[0]; // e.g. "sam-dbam-453954531541"
            const projectNum = firstPart.match(/-(\d{10,})$/);
            if (projectNum) {
                const num = projectNum[1];
                parts[0] = `${serviceName}-${num}`;
                return `${u.protocol}//${parts.join('.')}`;
            }
        }
    } catch (_) { /* invalid URL */ }
    return null;
}

const CLOUD_RUN_SERVICE_NAMES = {
    designer: 'cloudsam-designer',
    explorer: 'cloudsam-survey-explorer',
    console: 'cloudsam-console',
    channel_gate: 'channel-gate',
    kernel: 'cloudsam-kernel',
    survey_assistant: 'survey-assistant',
};

/**
 * Ask again without cross-origin rules, to tell "blocked" apart from "down".
 *
 * A service that sends no CORS header makes an ordinary fetch throw. So does a
 * service that is genuinely dead. The two are indistinguishable from the error
 * object, and the old code guessed by looking for the word "fetch" in the error
 * message. Chrome says "Failed to fetch" and passed the test. Safari says "Load
 * failed" and did not, so every iPad reported working services as red while
 * every desktop reported them green.
 *
 * A no-cors request removes the guess. The browser hands back an opaque response
 * whenever the server answered at all, and throws only when the connection
 * itself failed. Same result in every browser.
 */
async function confirmReachable(fullUrl, timeoutMs) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        await fetch(fullUrl, { method: 'GET', mode: 'no-cors', signal: controller.signal });
        return true;
    } catch (_) {
        return false;
    } finally {
        clearTimeout(timer);
    }
}

/**
 * Read identity-administration readiness out of sam-dbam's health body.
 * sam-dbam answers 200 whether or not identity admin is working — it is an
 * admin-only capability and must not take the service out of rotation — so the
 * HTTP status says nothing here and only the body does.
 */
async function checkIdentityAdmin(url, healthPath, { timeoutMs = PROBE_TIMEOUT_MS } = {}) {
    const fullUrl = `${url.replace(/\/+$/, '')}${healthPath}`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const tStart = Date.now();
    try {
        const resp = await fetch(fullUrl, { method: 'GET', signal: controller.signal });
        const latencyMs = Date.now() - tStart;
        const probe = { probedUrl: fullUrl, httpStatus: resp.status };
        let body = null;
        try { body = await resp.json(); } catch (_) { /* handled below */ }
        if (!body || typeof body.identity_admin !== 'string') {
            // An older sam-dbam that predates this field. Not a failure — say so
            // plainly rather than claiming health we cannot see.
            return { status: 'unknown', latencyMs, summary: 'Not reported', errorCode: null, checkIssue: true, ...probe };
        }
        const schema = body.hanko_schema ? ` (Hanko ${body.hanko_schema})` : '';
        if (body.identity_admin === 'ok') {
            return { status: 'green', latencyMs, summary: `Ready${schema}`, errorCode: null, checkIssue: false, ...probe };
        }
        // Both remaining cases need a person to change configuration. Neither is
        // fixed by a redeploy, so neither goes past yellow.
        if (body.identity_admin === 'not_configured') {
            return { status: 'yellow', latencyMs, summary: 'Not configured', errorCode: 'IDENTITY_ADMIN_NOT_CONFIGURED', checkIssue: false, ...probe };
        }
        return { status: 'yellow', latencyMs, summary: `Unavailable${schema}`, errorCode: 'IDENTITY_ADMIN_UNAVAILABLE', checkIssue: false, ...probe };
    } catch (err) {
        const latencyMs = Date.now() - tStart;
        return {
            status: 'unknown',
            latencyMs,
            summary: err.name === 'AbortError' ? 'Check timed out' : 'Unreadable',
            errorCode: err.name === 'AbortError' ? 'IDENTITY_ADMIN_PROBE_TIMEOUT' : null,
            checkIssue: true,
            probedUrl: fullUrl, httpStatus: null,
        };
    } finally {
        clearTimeout(timer);
    }
}

export async function checkServiceHealth(url, healthPath, { timeoutMs = PROBE_TIMEOUT_MS } = {}) {
    const fullUrl = healthPath ? `${url.replace(/\/+$/, '')}${healthPath}` : url;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const tStart = Date.now();
    try {
        const resp = await fetch(fullUrl, { method: 'GET', signal: controller.signal });
        const latencyMs = Date.now() - tStart;
        return {
            ...classifyHttpStatus(resp.status),
            latencyMs, probedUrl: fullUrl, httpStatus: resp.status,
        };
    } catch (err) {
        const latencyMs = Date.now() - tStart;
        if (err.name === 'AbortError') {
            return { ...timedOut(), latencyMs, probedUrl: fullUrl, httpStatus: null };
        }
        // Could be a cross-origin block or a dead service. Ask again in a way
        // that cross-origin rules cannot interfere with, and believe the answer.
        const answered = await confirmReachable(fullUrl, timeoutMs);
        return {
            ...(answered ? reachableOnly() : unreachable()),
            latencyMs, probedUrl: fullUrl, httpStatus: null,
        };
    } finally {
        clearTimeout(timer);
    }
}

/**
 * Serialize one poll's results for an alert packet. Carries the probe detail —
 * URL, HTTP status, error code — that a summary line cannot, because the packet
 * has to support root-cause investigation after the fact.
 *
 * `status` is the raw reading. `displayed_status` is what the operator actually
 * saw, after confirmation and after the critical/secondary rule. Recording both
 * is the point: it is the only way to answer "the button was green, was anything
 * wrong?" later on.
 */
export function serializeServices(results) {
    return results.map(r => ({
        key: r.key,
        label: r.label,
        status: r.status,
        displayed_status: r.displayedStatus ?? r.status,
        latency_ms: r.latencyMs ?? null,
        summary: r.summary || '',
        error_code: r.errorCode || null,
        http_status: r.httpStatus ?? null,
        check_issue: Boolean(r.checkIssue),
        probed_url: r.probedUrl || null,
        health_path: r.healthPath || null,
    }));
}

/** Compact one poll into a single history line: enough to read a pattern, small enough to store. */
function pollSnapshot(results, aggregate, at) {
    return {
        at: new Date(at).toISOString(),
        aggregate,
        services: results.reduce((acc, r) => {
            acc[r.key] = r.displayedStatus === r.status
                ? r.status
                : `${r.status}>${r.displayedStatus}`;
            return acc;
        }, {}),
    };
}

function readPendingAlerts() {
    try {
        const raw = window.localStorage.getItem(PENDING_ALERTS_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
        return [];
    }
}

function writePendingAlerts(queue) {
    try {
        window.localStorage.setItem(PENDING_ALERTS_KEY, JSON.stringify(queue.slice(-PENDING_ALERTS_LIMIT)));
    } catch (_) { /* storage full or blocked — the in-memory retry still runs */ }
}

/**
 * POST one record to the backend, which writes it to the audit store.
 *
 * Auto-archived records never email support — that stays reserved for an
 * operator pressing the button — so an outage that flaps for hours cannot
 * mail-bomb support while still leaving a record.
 *
 * @returns {Promise<boolean>} true when the backend accepted and stored it.
 */
async function postAlert(body) {
    const resp = await fetch('/api/console/system/support-alert', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    if (!resp.ok) return false;
    const outcome = await resp.json().catch(() => ({}));
    return outcome.archived !== false;
}

/**
 * Retry records that could not be written earlier.
 *
 * The commonest reason a write fails is an expired session: the audit route
 * needs a signed-in user. That used to drop the record on the floor without a
 * word, so an outage could pass with nothing in the log to show for it. Now the
 * record waits in local storage and goes out on a later poll.
 */
async function flushPendingAlerts() {
    const queue = readPendingAlerts();
    if (queue.length === 0) return;
    const stillPending = [];
    for (const entry of queue) {
        let sent = false;
        try {
            sent = await postAlert(entry.body);
        } catch (_) { /* offline — keep it */ }
        const attempts = (entry.attempts || 0) + 1;
        if (!sent && attempts < PENDING_ALERT_MAX_ATTEMPTS) {
            stillPending.push({ ...entry, attempts });
        }
    }
    writePendingAlerts(stillPending);
}

/** Park a record that could not be written, so a later poll can send it. */
function queuePendingAlert(body) {
    writePendingAlerts([...readPendingAlerts(), { body, attempts: 0 }]);
}

export function useSystemStatus({ corsBanner }) {
    const [services, setServices] = useState(() => SERVICE_DEFS.map(d => ({ ...d, status: 'unknown', displayedStatus: 'unknown', latencyMs: null, summary: '' })));
    const [aggregateStatus, setAggregateStatus] = useState('unknown');
    const [lastCheckedAt, setLastCheckedAt] = useState(null);
    const [serviceUrls, setServiceUrls] = useState(null);
    const pollTimerRef = useRef(null);
    const startedAtRef = useRef(Date.now());
    // Open failure episodes, keyed by service. Survives re-renders so the dwell
    // window is measured across polls rather than restarted by React.
    const episodesRef = useRef(new Map());
    // Consecutive-failure counts. An alarm has to be seen twice running before
    // it is shown, so no single blip can turn the button or the website red.
    const confirmRef = useRef(createStatusConfirmer());
    // A rolling window of recent polls, attached to every record we file.
    const historyRef = useRef([]);

    // Fetch sysconfig once to get service URLs
    useEffect(() => {
        (async () => {
            try {
                const base = process.env.REACT_APP_SAM_DBAM_URL;
                if (!base) return;
                const resp = await fetch(`${base.replace(/\/+$/, '')}/dbam/v1/config`);
                if (resp.ok) {
                    const json = await resp.json();
                    const svc = json?.data?.services || json?.services || {};
                    setServiceUrls(svc);
                }
            } catch (_) { /* sysconfig fetch failed — will use fallbacks */ }
        })();
    }, []);

    const pollAll = useCallback(async () => {
        const samDbamUrl = process.env.REACT_APP_SAM_DBAM_URL?.replace(/\/+$/, '');
        const results = await Promise.all(SERVICE_DEFS.map(async (def) => {
            if (def.isSelf) {
                // Console is green if we're running. Only a structural client
                // fault counts against it — the CORS banner means the kernel is
                // rejecting this origin, which no user action can clear. Ordinary
                // operation failures ("cannot undeploy", "no results to export")
                // are surfaced in their own banner and are not service health:
                // they are sticky, so folding them in here left Console amber for
                // the rest of the session and, past the dwell window, filed a
                // failure episode for what was just a user-visible error message.
                const hasError = Boolean(corsBanner);
                return {
                    ...def, status: hasError ? 'yellow' : 'green', latencyMs: 0,
                    summary: hasError ? 'Origin rejected' : 'OK',
                    errorCode: hasError ? 'CONSOLE_CORS_REJECTED' : null,
                    checkIssue: false, probedUrl: null, httpStatus: null,
                };
            }

            // Resolve URL: sysconfig → fallback derivation
            let url = serviceUrls?.[def.configKey];
            if (!url && def.fallbackUrl) url = def.fallbackUrl;
            if (!url && def.configKey === 'sam_dbam') url = samDbamUrl;
            if (!url && samDbamUrl && CLOUD_RUN_SERVICE_NAMES[def.key]) {
                url = deriveFallbackUrl(samDbamUrl, CLOUD_RUN_SERVICE_NAMES[def.key]);
            }
            if (!url) {
                // We never worked out where to look. That is a gap in the checker,
                // not proof the service stopped, so it is logged and not alarmed.
                return { ...def, ...notChecked(), latencyMs: null, probedUrl: null, httpStatus: null };
            }

            if (def.isIdentityAdmin) {
                const result = await checkIdentityAdmin(url, def.healthPath);
                return { ...def, ...result };
            }

            const result = await checkServiceHealth(url, def.healthPath);
            return { ...def, ...result };
        }));

        // Hold each alarm back until a second poll agrees with it. The raw
        // reading is kept alongside so the audit record shows both what was
        // measured and what the operator was actually shown.
        const confirmed = results.map(r => ({
            ...r,
            displayedStatus: confirmRef.current(r.key, r.status),
        }));

        const agg = foldAggregate(confirmed.map(r => ({ key: r.key, status: r.displayedStatus })));

        setServices(confirmed);
        setAggregateStatus(agg);
        setLastCheckedAt(new Date());

        historyRef.current = [...historyRef.current, pollSnapshot(confirmed, agg, Date.now())]
            .slice(-POLL_HISTORY_LIMIT);

        // Every failure earns a record, including the short ones. Long or
        // structural failures are filed as they happen; brief ones are filed
        // once they clear, so a red button always has something behind it.
        try {
            const due = updateEpisodes(episodesRef.current, confirmed);
            for (const episode of due) {
                const body = buildEpisodeBody(episode, confirmed, agg, historyRef.current);
                postAlert(body)
                    .then((sent) => { if (!sent) queuePendingAlert(body); })
                    .catch(() => queuePendingAlert(body));
            }
        } catch (_) { /* never let audit bookkeeping break polling */ }

        flushPendingAlerts().catch(() => { /* retried next poll */ });

        // Adaptive polling interval. Any raw failure speeds us up, even one that
        // is not being shown yet, so the second confirming poll lands quickly and
        // a genuine outage is not delayed by the confirmation rule.
        const anySuspect = results.some(r => r.status === 'red' || r.status === 'yellow');
        const elapsed = Date.now() - startedAtRef.current;
        if (anySuspect) return 10000;
        if (elapsed < 5 * 60 * 1000) return 15000;
        return 45000;
    }, [serviceUrls, corsBanner]);

    useEffect(() => {
        // Don't start polling until sysconfig is loaded (or after 3s timeout)
        if (serviceUrls === null) {
            const fallbackTimer = setTimeout(() => setServiceUrls({}), 3000);
            return () => clearTimeout(fallbackTimer);
        }

        async function loop() {
            const delay = await pollAll().catch(() => 10000);
            if (!document.hidden) pollTimerRef.current = setTimeout(loop, delay);
        }
        loop();

        function onVisibility() {
            if (document.hidden && pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
            if (!document.hidden) { if (pollTimerRef.current) clearTimeout(pollTimerRef.current); pollTimerRef.current = setTimeout(loop, 0); }
        }
        document.addEventListener('visibilitychange', onVisibility);
        return () => { document.removeEventListener('visibilitychange', onVisibility); if (pollTimerRef.current) clearTimeout(pollTimerRef.current); };
    }, [serviceUrls, pollAll]);

    const manualRefresh = useCallback(async () => {
        if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
        await pollAll().catch(() => { });
    }, [pollAll]);

    return { services, aggregateStatus, lastCheckedAt, manualRefresh };
}

/** Build the packet body for one episode. Shared by the live path and the retry queue. */
function buildEpisodeBody(episode, results, aggregate, history) {
    return {
        trigger: episode.classification === 'definite' ? 'definite_failure'
            : episode.classification === 'transient' ? 'transient_failure'
                : 'persistent_failure',
        notify: false,
        episode: {
            key: episode.key,
            service_key: episode.service_key,
            service_label: episode.service_label,
            classification: episode.classification,
            started_at: episode.started_at,
            last_seen_at: episode.last_seen_at,
            ended_at: episode.ended_at || null,
            dwell_ms: episode.dwell_ms ?? null,
            observation_count: episode.observations.length,
            observations: episode.observations,
            error_codes: episode.error_codes || [],
            probed_url: episode.probed_url || null,
            worst_status: episode.worst_status || null,
            shown_to_operator: Boolean(episode.shown_to_operator),
        },
        aggregate_status: aggregate,
        services: serializeServices(results),
        error_codes: Array.from(new Set(results.map(r => r.errorCode).filter(Boolean))),
        recent_polls: history,
        url: window.location.href,
        user_agent: navigator.userAgent,
        last_checked_at: new Date().toISOString(),
    };
}
